import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@/shared/components/Dialog';
import { PillButton } from '@/shared/components/PillButton';

// Safari Share icon (square with arrow pointing up)
function ShareIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}

// Add to Home Screen icon (plus inside a square)
function AddToHomeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <line x1="12" y1="8" x2="12" y2="16" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  );
}

export interface PWAInstallDialogProps {
  isOpen: boolean;
  isSafari?: boolean;
  onInstall: () => void;
  onClose: () => void;
}

export const PWAInstallDialog = memo(function PWAInstallDialog({
  isOpen,
  isSafari = false,
  onInstall,
  onClose
}: PWAInstallDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      variant="green"
      title={t('pwaInstall.title')}
      allowClickOutsideToClose={true}
    >
      <div className="space-y-4">
        {/* App Icon */}
        <div className="flex justify-center">
          <img src="/pwa-192x192.png" alt="LUDOKA" className="w-20 h-20 rounded-2xl shadow-lg" />
        </div>

        {/* Description */}
        <p className="font-mind-boggle text-sm text-[#333] text-center leading-tight">
          {isSafari ? t('pwaInstall.safariDescription') : t('pwaInstall.description')}
        </p>

        {/* Safari Instructions */}
        {isSafari && (
          <div className="bg-gray-100 rounded-xl p-3 space-y-3">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-[#007AFF] rounded-lg flex items-center justify-center">
                <ShareIcon className="w-5 h-5 text-white" />
              </div>
              <p className="font-mind-boggle text-xs text-[#333] leading-tight flex-1">
                {t('pwaInstall.safariStep1')}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-[#007AFF] rounded-lg flex items-center justify-center">
                <AddToHomeIcon className="w-5 h-5 text-white" />
              </div>
              <p className="font-mind-boggle text-xs text-[#333] leading-tight flex-1">
                {t('pwaInstall.safariStep2')}
              </p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col gap-2">
          {!isSafari && (
            <PillButton variant="green" onClick={onInstall} className="w-full h-12">
              {t('pwaInstall.install')}
            </PillButton>
          )}
          <PillButton variant="grey" onClick={onClose} className="w-full h-12">
            {isSafari ? t('pwaInstall.gotIt') : t('pwaInstall.notNow')}
          </PillButton>
        </div>
      </div>
    </Dialog>
  );
});

export default PWAInstallDialog;
